import { useState } from 'react';

type PayMethod = 'tabby' | 'card' | 'apple_pay' | 'google_pay' | 'cod';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
}

interface FormData {
  name: string;
  phone: string;
  email: string;
  city: string;
  address: string;
}

interface Props {
  form: FormData;
  total: number;
  shipping: number;
  items: CartItem[];
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onBack: () => void;
  onConfirm: (method: PayMethod) => void;
}

const UAE_CITIES = ['دبي', 'أبوظبي', 'الشارقة', 'عجمان', 'رأس الخيمة', 'الفجيرة', 'أم القيوين'];
const STRIPE_URL = 'https://lhcjpiopscgpttrbucqz.supabase.co/functions/v1/stripe-checkout';
const TABBY_URL = 'https://lhcjpiopscgpttrbucqz.supabase.co/functions/v1/tabby-checkout';

export default function CheckoutStep({ form, total, shipping, items, onChange, onBack, onConfirm }: Props) {
  const [method, setMethod] = useState<PayMethod>('cod');
  const [processing, setProcessing] = useState(false);
  const [payError, setPayError] = useState('');
  const [fallbackUrl, setFallbackUrl] = useState('');

  const installment = Math.ceil(total / 4);
  const isFormValid = form.name.trim() && form.phone.trim() && form.city.trim() && form.address.trim();

  const getProcessingLabel = () => {
    if (method === 'card' || method === 'apple_pay' || method === 'google_pay') return 'جاري التحويل لصفحة الدفع الآمنة...';
    if (method === 'tabby') return 'جاري التحويل لصفحة Tabby...';
    return 'جاري تأكيد طلبك...';
  };

  const getButtonLabel = () => {
    if (method === 'card') return 'الدفع عبر Stripe';
    if (method === 'apple_pay') return 'الدفع عبر Apple Pay';
    if (method === 'google_pay') return 'الدفع عبر Google Pay';
    if (method === 'tabby') return 'الدفع عبر Tabby';
    return 'تأكيد الطلب';
  };

  const getButtonIcon = () => {
    if (method === 'card') return 'ri-secure-payment-line';
    if (method === 'apple_pay') return 'ri-apple-fill';
    if (method === 'google_pay') return 'ri-google-fill';
    if (method === 'tabby') return 'ri-exchange-dollar-line';
    return 'ri-shield-check-line';
  };

  const redirectTo = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSubmit = async () => {
    if (!isFormValid) return;
    setProcessing(true);
    setPayError('');
    setFallbackUrl('');

    const origin = window.location.origin;
    const base = import.meta.env.BASE_URL?.replace(/\/$/, '') || '';

    if (method === 'card' || method === 'apple_pay' || method === 'google_pay') {
      try {
        const res = await fetch(STRIPE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: items.map(i => ({ name: i.name, price: i.price, quantity: i.quantity })),
            totalPrice: total - shipping,
            shipping,
            customerInfo: { name: form.name, phone: form.phone, email: form.email || undefined, city: form.city, address: form.address },
            successUrl: `${origin}${base}/payment?stripe_success=true`,
            cancelUrl: `${origin}${base}/payment?stripe_cancel=true`,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `خطأ في الاتصال (${res.status})`);
        if (data.url) {
          // حفظ بيانات الطلب قبل المغادرة لـ Stripe
          const trackNum = `TR-${Date.now().toString(36).toUpperCase()}-${Math.floor(Math.random() * 9000 + 1000)}`;
          localStorage.setItem('pending_order', JSON.stringify({
            items,
            form,
            total,
            shipping,
            method,
            orderId: `#${Math.floor(Math.random() * 90000 + 10000)}`,
            trackingNumber: trackNum,
            timestamp: new Date().toISOString(),
          }));
          // نعرض الزر فوراً ونحاول الريدايركت تلقائياً
          setFallbackUrl(data.url);
          setProcessing(false);
          redirectTo(data.url);
        } else {
          throw new Error(data.error || 'لم يتم الحصول على رابط الدفع');
        }
      } catch (e) {
        setPayError((e as Error).message || 'فشل الاتصال بـ Stripe');
        setFallbackUrl('');
        setProcessing(false);
      }
      return;
    }

    if (method === 'tabby') {
      try {
        const res = await fetch(TABBY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: items.map(i => ({ name: i.name, price: i.price, quantity: i.quantity })),
            totalPrice: total - shipping,
            shipping,
            customerInfo: { name: form.name, phone: form.phone, email: form.email, city: form.city, address: form.address },
            successUrl: `${origin}${base}/payment?tabby_success=true`,
            cancelUrl: `${origin}${base}/payment?tabby_cancel=true`,
            failureUrl: `${origin}${base}/payment?tabby_cancel=true`,
          }),
        });
        if (!res.ok) throw new Error(`خطأ في الاتصال (${res.status})`);
        const data = await res.json();
        if (data.url) {
          const trackNum = `TR-${Date.now().toString(36).toUpperCase()}-${Math.floor(Math.random() * 9000 + 1000)}`;
          localStorage.setItem('pending_order', JSON.stringify({
            items,
            form,
            total,
            shipping,
            method,
            orderId: `#${Math.floor(Math.random() * 90000 + 10000)}`,
            trackingNumber: trackNum,
            timestamp: new Date().toISOString(),
          }));
          setFallbackUrl(data.url);
          setProcessing(false);
          redirectTo(data.url);
        } else {
          throw new Error(data.error || 'فشل الاتصال بـ Tabby');
        }
      } catch (e) {
        setPayError((e as Error).message || 'فشل الاتصال بـ Tabby');
        setFallbackUrl('');
        setProcessing(false);
      }
      return;
    }

    // COD
    setTimeout(() => {
      setProcessing(false);
      onConfirm(method);
    }, 800);
  };

  return (
    <div className="relative" dir="rtl">
      {processing && (
        <div className="fixed inset-0 bg-white/95 z-50 flex flex-col items-center justify-center gap-4 px-6">
          <div className="w-14 h-14 rounded-full border-4 border-gray-200 border-t-[#C9A961] animate-spin"
            style={{ borderTopColor: method === 'tabby' ? '#3DBDA7' : '#C9A961' }} />
          <p className="text-gray-700 font-bold text-center">{getProcessingLabel()}</p>
        </div>
      )}

      {!processing && fallbackUrl && (
        <div className="fixed inset-0 bg-white z-50 flex flex-col items-center justify-center gap-5 px-6">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ background: method === 'tabby' ? '#3DBDA7' : '#C9A961' }}
          >
            <i className={`${getButtonIcon()} text-white text-2xl`} />
          </div>
          <div className="text-center">
            <p className="text-gray-900 font-black text-lg mb-1">جاهز للدفع!</p>
            <p className="text-gray-500 text-sm">اضغط الزر أدناه للانتقال لصفحة الدفع الآمنة</p>
          </div>
          <a
            href={fallbackUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-10 py-4 rounded-2xl font-black text-white text-base cursor-pointer whitespace-nowrap w-full max-w-xs text-center"
            style={{ background: method === 'tabby' ? '#3DBDA7' : '#C9A961' }}
          >
            <i className="ri-arrow-left-line" />
            {method === 'tabby' ? 'الانتقال لـ Tabby' : 'الانتقال لصفحة الدفع'}
          </a>
          <button
            onClick={() => setFallbackUrl('')}
            className="text-gray-400 text-sm underline cursor-pointer"
          >
            إلغاء والرجوع
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* LEFT: DELIVERY INFO */}
        <div className="bg-white rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 flex items-center justify-center bg-[#C9A961] rounded-lg">
              <i className="ri-map-pin-line text-white text-sm" />
            </div>
            <h2 className="text-lg font-black text-gray-900">بيانات التوصيل</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5">الاسم الكامل <span className="text-red-400">*</span></label>
              <div className="relative">
                <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line text-gray-400 text-sm" />
                </div>
                <input type="text" name="name" value={form.name} onChange={onChange} placeholder="محمد أحمد"
                  className="w-full pr-9 pl-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 focus:border-[#C9A961]" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5">رقم الواتساب <span className="text-red-400">*</span></label>
              <div className="relative">
                <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-gray-400 text-sm" />
                </div>
                <input type="tel" name="phone" value={form.phone} onChange={onChange} placeholder="+971 5X XXX XXXX"
                  className="w-full pr-9 pl-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 focus:border-[#C9A961]" dir="ltr" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5">الإمارة <span className="text-red-400">*</span></label>
              <div className="relative">
                <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-2-line text-gray-400 text-sm" />
                </div>
                <select name="city" value={form.city} onChange={onChange}
                  className="w-full pr-9 pl-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 focus:border-[#C9A961] appearance-none cursor-pointer bg-white">
                  <option value="">اختر الإمارة</option>
                  {UAE_CITIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5">العنوان التفصيلي <span className="text-red-400">*</span></label>
              <div className="relative">
                <div className="absolute right-3 top-3.5 w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-4-line text-gray-400 text-sm" />
                </div>
                <input type="text" name="address" value={form.address} onChange={onChange} placeholder="المنطقة، الشارع، رقم المبنى"
                  className="w-full pr-9 pl-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 focus:border-[#C9A961]" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5">البريد الإلكتروني <span className="text-gray-400 font-normal">(اختياري)</span></label>
              <div className="relative">
                <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line text-gray-400 text-sm" />
                </div>
                <input type="email" name="email" value={form.email} onChange={onChange} placeholder="example@email.com"
                  className="w-full pr-9 pl-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 focus:border-[#C9A961]" dir="ltr" />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2.5 mt-5 bg-[#C9A961]/6 rounded-xl p-3 border border-[#C9A961]/15">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
              <i className="ri-truck-line text-[#C9A961] text-lg" />
            </div>
            <div>
              <p className="text-xs font-bold text-gray-700">توصيل خلال 2-5 أيام عمل</p>
              <p className="text-xs text-gray-400">مجاني للطلبات فوق 300 د.إ</p>
            </div>
          </div>
        </div>

        {/* RIGHT: PAYMENT METHOD */}
        <div className="bg-white rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 flex items-center justify-center bg-[#C9A961] rounded-lg">
              <i className="ri-bank-card-line text-white text-sm" />
            </div>
            <h2 className="text-lg font-black text-gray-900">طريقة الدفع</h2>
          </div>

          <div className="space-y-3">

            {/* COD */}
            <div onClick={() => setMethod('cod')}
              className={`cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${method === 'cod' ? 'border-[#C9A961] bg-[#C9A961]/5' : 'border-gray-200 hover:border-[#C9A961]/40'}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-[#F5F1E8] rounded-xl flex-shrink-0">
                  <i className="ri-cash-line text-xl text-[#C9A961]" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-gray-800 text-sm">الدفع عند الاستلام</p>
                  <p className="text-xs text-gray-400">نقداً أو ببطاقتك عند وصول طلبك</p>
                </div>
                <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${method === 'cod' ? 'text-[#C9A961]' : 'text-gray-200'}`}>
                  <i className="ri-checkbox-circle-fill text-xl" />
                </div>
              </div>
            </div>

            {/* TABBY */}
            <div onClick={() => setMethod('tabby')}
              className={`cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${method === 'tabby' ? 'border-[#3DBDA7] bg-[#3DBDA7]/5' : 'border-gray-200 hover:border-[#3DBDA7]/40'}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-[#3DBDA7]/15 rounded-xl flex-shrink-0">
                  <span className="text-[#3DBDA7] font-black text-sm">4×</span>
                </div>
                <div className="flex-1">
                  <p className="font-bold text-[#3DBDA7] text-sm">Tabby — تقسيط بدون فوائد</p>
                  <p className="text-xs text-gray-400">4 دفعات متساوية كل شهر</p>
                </div>
                <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${method === 'tabby' ? 'text-[#3DBDA7]' : 'text-gray-200'}`}>
                  <i className="ri-checkbox-circle-fill text-xl" />
                </div>
              </div>
              {method === 'tabby' && (
                <>
                  <div className="grid grid-cols-4 gap-1.5 mt-3">
                    {['اليوم', 'شهر 1', 'شهر 2', 'شهر 3'].map((l, i) => (
                      <div key={i} className={`rounded-lg p-2 text-center border ${i === 0 ? 'bg-[#3DBDA7] border-[#3DBDA7]' : 'bg-white border-[#3DBDA7]/30'}`}>
                        <div className={`text-[10px] ${i === 0 ? 'text-white/70' : 'text-gray-400'}`}>{l}</div>
                        <div className={`text-xs font-black ${i === 0 ? 'text-white' : 'text-[#3DBDA7]'}`}>{installment} د.إ</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-2 bg-[#3DBDA7]/8 rounded-xl px-3 py-2.5">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-keyhole-line text-[#3DBDA7] text-sm" />
                    </div>
                    <p className="text-xs text-gray-600">ستُحوَّل لصفحة Tabby الآمنة. أول دفعة اليوم والباقي على 3 أشهر بدون فوائد.</p>
                  </div>
                </>
              )}
            </div>

            {/* APPLE PAY */}
            <div onClick={() => setMethod('apple_pay')}
              className={`cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${method === 'apple_pay' ? 'border-gray-900 bg-gray-900' : 'border-gray-200 hover:border-gray-400'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${method === 'apple_pay' ? 'bg-white/15' : 'bg-gray-100'}`}>
                  <i className={`ri-apple-fill text-xl ${method === 'apple_pay' ? 'text-white' : 'text-gray-700'}`} />
                </div>
                <div className="flex-1">
                  <p className={`font-bold text-sm ${method === 'apple_pay' ? 'text-white' : 'text-gray-800'}`}>Apple Pay</p>
                  <p className={`text-xs ${method === 'apple_pay' ? 'text-white/60' : 'text-gray-400'}`}>iPhone · iPad · Mac — Face ID أو Touch ID</p>
                </div>
                <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${method === 'apple_pay' ? 'text-white' : 'text-gray-200'}`}>
                  <i className="ri-checkbox-circle-fill text-xl" />
                </div>
              </div>
              {method === 'apple_pay' && (
                <div className="mt-3 flex items-start gap-2 bg-white/10 rounded-xl px-3 py-2.5">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-fingerprint-line text-white text-sm" />
                  </div>
                  <p className="text-xs text-white/70">ستنتقل لصفحة Stripe الآمنة — يظهر زر Apple Pay تلقائياً على Safari (iPhone / iPad / Mac). على الأجهزة الأخرى تقدر تدفع بالبطاقة.</p>
                </div>
              )}
            </div>

            {/* GOOGLE PAY */}
            <div onClick={() => setMethod('google_pay')}
              className={`cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${method === 'google_pay' ? 'border-[#34A853] bg-[#34A853]/5' : 'border-gray-200 hover:border-[#34A853]/40'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${method === 'google_pay' ? 'bg-[#34A853]/15' : 'bg-gray-100'}`}>
                  <i className={`ri-google-fill text-xl ${method === 'google_pay' ? 'text-[#34A853]' : 'text-gray-500'}`} />
                </div>
                <div className="flex-1">
                  <p className={`font-bold text-sm ${method === 'google_pay' ? 'text-[#34A853]' : 'text-gray-800'}`}>Google Pay</p>
                  <p className={`text-xs ${method === 'google_pay' ? 'text-[#34A853]/70' : 'text-gray-400'}`}>Android · Chrome · Windows · Mac</p>
                </div>
                <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${method === 'google_pay' ? 'text-[#34A853]' : 'text-gray-200'}`}>
                  <i className="ri-checkbox-circle-fill text-xl" />
                </div>
              </div>
              {method === 'google_pay' && (
                <div className="mt-3 flex items-start gap-2 bg-[#34A853]/8 rounded-xl px-3 py-2.5">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-shield-keyhole-line text-[#34A853] text-sm" />
                  </div>
                  <p className="text-xs text-gray-600">ستنتقل لصفحة Stripe الآمنة — يظهر زر Google Pay تلقائياً على Chrome و Android. على الأجهزة الأخرى تقدر تدفع بالبطاقة.</p>
                </div>
              )}
            </div>

            {/* STRIPE CARD */}
            <div onClick={() => setMethod('card')}
              className={`cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${method === 'card' ? 'border-[#C9A961] bg-[#C9A961]/5' : 'border-gray-200 hover:border-[#C9A961]/40'}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-[#F5F1E8] rounded-xl flex-shrink-0">
                  <i className="ri-bank-card-line text-xl text-[#C9A961]" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-gray-800 text-sm">الدفع بالبطاقة — Stripe</p>
                  <p className="text-xs text-gray-400">فيزا · ماستركارد · مدى — دفع آمن ومباشر</p>
                </div>
                <div className="flex items-center gap-1">
                  {['VISA', 'MC'].map(b => (
                    <span key={b} className="bg-gray-100 text-gray-500 text-[10px] font-bold px-1.5 py-0.5 rounded border border-gray-200">{b}</span>
                  ))}
                </div>
                <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${method === 'card' ? 'text-[#C9A961]' : 'text-gray-200'}`}>
                  <i className="ri-checkbox-circle-fill text-xl" />
                </div>
              </div>
              {method === 'card' && (
                <div className="mt-3 flex items-center gap-2 bg-[#C9A961]/6 rounded-xl px-3 py-2.5">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-keyhole-line text-[#C9A961] text-sm" />
                  </div>
                  <p className="text-xs text-gray-600">ستُحوَّل لصفحة Stripe الآمنة لإدخال بيانات بطاقتك. بياناتك محمية ولا تمر عبر موقعنا.</p>
                </div>
              )}
            </div>
          </div>

          {/* ── PAYMENT TRUST STRIP ── */}
          <div className="mt-4 bg-[#FAFAF8] rounded-xl border border-gray-100 px-4 py-3">
            <p className="text-[10px] font-bold text-gray-400 mb-2.5 text-center">طرق الدفع المقبولة — دفع آمن ومشفر</p>
            <div className="flex items-center justify-center gap-2 flex-wrap">

              {/* Visa */}
              <div className="bg-white rounded-md px-2.5 py-1.5 flex items-center justify-center h-8 border border-gray-200 min-w-[46px]">
                <span className="text-[#1A1F71] font-black text-sm italic tracking-widest leading-none">VISA</span>
              </div>

              {/* Mastercard */}
              <div className="bg-white rounded-md px-2 py-1.5 flex items-center gap-1 h-8 border border-gray-200">
                <div className="flex items-center relative">
                  <div className="w-4 h-4 rounded-full bg-[#EB001B]" />
                  <div className="w-4 h-4 rounded-full bg-[#F79E1B] -mr-2 opacity-90" />
                </div>
                <span className="text-[9px] font-black text-gray-500 mr-0.5 leading-none">MC</span>
              </div>

              {/* Apple Pay */}
              <div className="bg-black rounded-md px-2.5 py-1.5 flex items-center gap-1 h-8">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-apple-fill text-white text-sm" />
                </div>
                <span className="text-white font-bold text-[11px] leading-none">Pay</span>
              </div>

              {/* Google Pay */}
              <div className="bg-white rounded-md px-2 py-1.5 border border-gray-200 flex items-center gap-0.5 h-8">
                <span className="font-black text-sm leading-none">
                  <span className="text-[#4285F4]">G</span><span className="text-[#34A853]">o</span><span className="text-[#FBBC05]">o</span><span className="text-[#EA4335]">g</span>
                </span>
                <span className="text-gray-700 font-bold text-[11px] leading-none">Pay</span>
              </div>

              {/* Tabby */}
              <div className="bg-[#3DBDA7] rounded-md px-2.5 py-1.5 flex items-center gap-1 h-8">
                <span className="text-white font-black text-[11px] leading-none">tabby</span>
                <span className="bg-white/25 text-white text-[9px] font-black px-1 py-0.5 rounded leading-none">4×</span>
              </div>

              {/* mada */}
              <div className="bg-white rounded-md px-2.5 py-1.5 flex items-center justify-center h-8 border border-gray-200">
                <span className="text-[#00A651] font-black text-[11px] leading-none">mada</span>
              </div>

            </div>
            <div className="flex items-center justify-center gap-4 mt-2.5 pt-2.5 border-t border-gray-100">
              <span className="flex items-center gap-1 text-[10px] text-gray-400">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-lock-2-line text-[#C9A961] text-xs" />
                </div>
                SSL 256-bit
              </span>
              <span className="flex items-center gap-1 text-[10px] text-gray-400">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-shield-check-line text-[#C9A961] text-xs" />
                </div>
                دفع آمن
              </span>
              <span className="flex items-center gap-1 text-[10px] text-gray-400">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-refund-2-line text-[#C9A961] text-xs" />
                </div>
                ضمان استرداد
              </span>
            </div>
          </div>

          {payError && (
            <div className="mt-3 flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-500" />
              </div>
              <p className="text-red-600 text-xs font-bold">{payError}</p>
            </div>
          )}

          {/* Order total */}
          <div className="mt-5 bg-[#FAFAF8] rounded-xl p-4 border border-gray-100">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-500 text-sm">المنتجات</span>
              <span className="font-bold text-gray-800 text-sm">{total - shipping} د.إ</span>
            </div>
            <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-200">
              <span className="text-gray-500 text-sm">الشحن</span>
              <span className={`font-bold text-sm ${shipping === 0 ? 'text-green-600' : 'text-gray-800'}`}>
                {shipping === 0 ? 'مجاني' : `${shipping} د.إ`}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-black text-gray-900">الإجمالي</span>
              <span className="font-black text-[#C9A961] text-xl">{total} د.إ</span>
            </div>
          </div>

          <div className="flex gap-3 mt-5">
            <button onClick={onBack}
              className="flex items-center justify-center gap-2 px-5 py-3.5 border border-gray-200 text-gray-600 rounded-xl font-bold cursor-pointer hover:bg-gray-50 whitespace-nowrap transition-colors">
              <i className="ri-arrow-right-line" />
              رجوع
            </button>
            <button
              onClick={handleSubmit}
              disabled={!isFormValid || processing}
              className={`flex-1 flex items-center justify-center gap-2 text-white py-3.5 rounded-xl font-black cursor-pointer transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${
                method === 'tabby' ? 'bg-[#3DBDA7] hover:bg-[#2DA090]' :
                method === 'apple_pay' ? 'bg-gray-900 hover:bg-black' :
                method === 'google_pay' ? 'bg-[#34A853] hover:bg-[#2a8a44]' :
                'bg-[#C9A961] hover:bg-[#A07C30]'
              }`}>
              <i className={getButtonIcon()} />
              <span>{getButtonLabel()}</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
